import React, { useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { userDataSelectors } from '../../../redux/userData';
import {
  MemberInfoCell,
  priceFormat,
} from '../../../components/tableCellRenders';
import {
  getAccountDummyProjects,
  getSelectedProject,
  getVisibleAccountDummyMembers,
  projectedRevenuePrompt,
  selectHiddenAccountMembers,
  toggleResetAll,
  setGroupBy,
  GroupAccountMembersBy,
  toggleEditableMode,
  initMembersWithDummyMarginData,
} from '../../../redux/account';
import { AccountDetail, DummyMember, ProjectDetail } from '../../../types';
import { avatarMediumSize } from '../../../theme';
import { Info } from '../../../features/budgets/BudgetsHeader/Info/Info';
import { CustomNumber } from '../../../features/budgets/BudgetsHeader/Number/Number';
import { GrossMarginSummary } from '../../../features/budgets/BudgetsHeader/GrossMarginSummary/GrossMarginSummary';
import {
  formatDateTo_MMM_dd_yyyy,
  getRevenueLastInvoiceDateFormated,
} from '../../../utils';
import {
  IconName,
  CustomIcon,
} from '../../../components/customIcon/CustomIcon';
import { COLORS } from '../../../utils/colors';
import { MyPortfolioHeaderPopover } from './MyPortfolioHeaderPopOver';
import EditHeadcountChangeModal from '../../../features/editHeadcountChangesModal/EditHeadcountChangesModal';
import { CsvDownloadButton } from '../../../features/budgets/DownloadCsv/DownloadCsv';

interface Props {
  account: AccountDetail;
  projects: ProjectDetail[] | null;
  handleOnOpenRaiseBudgetModal?: () => void;
}

export const MyPortfolioHeaderForDetails = ({ account, projects }: Props) => {
  const visibleAccountDummyMembers: DummyMember[] | undefined = useAppSelector(
    getVisibleAccountDummyMembers,
  );

  const dispatch = useAppDispatch();

  const hiddenAccountMembers = useAppSelector(selectHiddenAccountMembers);
  const dummyProjects = useAppSelector(getAccountDummyProjects);
  const project = useAppSelector(getSelectedProject);
  const budget = project ? project : account;

  const user = useAppSelector(userDataSelectors.getUserData);
  const hasAccounts = Boolean(user?.permissions?.hasAccounts);

  const alertMailAnchor = React.useRef<HTMLElement | null>(null);
  const buttonMailAnchor = React.useRef<HTMLElement | null>(null);
  const [openMailPopover, setOpenMailPopover] = React.useState<boolean>(false);

  const [openAddButtonAlert, setOpenAddButtonAlert] =
    React.useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleToggleModal = useCallback(() => {
    setIsModalOpen(!isModalOpen);
  }, [isModalOpen, setIsModalOpen]);

  const handleAlertMailMouseOver = useCallback(() => {
    setOpenMailPopover(true);
  }, []);

  const handleAlertMailClose = useCallback(() => {
    setOpenMailPopover(false);
  }, []);

  const handleButtonMouseOver = useCallback(() => {
    setOpenAddButtonAlert(true);
  }, []);

  const handleButtonMouseClose = useCallback(() => {
    setOpenAddButtonAlert(false);
  }, []);

  const headcountSummary = useMemo(() => {
    if (project) return project.headcountSummary;
    return account?.headcountSummary;
  }, [account, project]);

  const { membersInTeams } = useMemo(() => {
    return (
      projects?.reduce(
        (
          { membersInTeams }: { membersInTeams: number },
          project: ProjectDetail,
        ) => ({
          membersInTeams: membersInTeams + project.membersCount,
        }),
        { membersInTeams: 0 },
      ) ?? { membersInTeams: 0 }
    );
  }, [projects]);

  const updatedMembers = useMemo(() => {
    return visibleAccountDummyMembers?.length
      ? membersInTeams + visibleAccountDummyMembers.length
      : membersInTeams;
  }, [visibleAccountDummyMembers, membersInTeams]);

  const revenueLastInvoiceDateFormated = getRevenueLastInvoiceDateFormated(
    budget.revenueLastInvoiceDate ?? null,
  );

  const calculateMargins = useCallback(() => {
    dispatch(toggleEditableMode());
    dispatch(setGroupBy(GroupAccountMembersBy.Team));
    dispatch(toggleResetAll());
    dispatch(initMembersWithDummyMarginData());
  }, [dispatch]);

  const projectedHeadcountMessage = useMemo(() => {
    if (!headcountSummary) return '';
    const joiningCount = Number(
      headcountSummary.projectedJoiningHeadcount || 0,
    );
    const leavingCount = Number(
      headcountSummary.projectedLeavingHeadcount || 0,
    );
    const formattedDate = formatDateTo_MMM_dd_yyyy(
      new Date(headcountSummary?.projectedHeadcountStartDate),
    );

    const joinText = joiningCount
      ? `${joiningCount} ${
          joiningCount > 1 ? 'resources' : 'resource'
        } will join`
      : '';
    const leaveText = leavingCount
      ? `${Math.abs(leavingCount)} ${
          Math.abs(leavingCount) > 1 ? 'resources' : 'resource'
        } will leave`
      : '';
    const separator = joiningCount && leavingCount ? ' and ' : '';

    return `${joinText}${separator}${leaveText} on ${formattedDate}`;
  }, [headcountSummary]);

  if (!hasAccounts) {
    return null;
  }

  return (
    <Card
      sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        padding: '16px 12px 16px 24px',
        borderRadius: (theme) => theme.spacing(1, 1, 0, 0),
        display: 'grid',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 3fr 3fr min-content',
          alignItems: 'center',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box>
          <Info title="Teams">
            <CustomNumber
              typographyStyle={{
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
              }}
              superscript={dummyProjects?.length ?? 0}
            >
              {(projects?.length ?? 0) + (dummyProjects?.length ?? 0)}
            </CustomNumber>
          </Info>
        </Box>
        <Box>
          <Info title="Headcount">
            <CustomNumber
              superscript={visibleAccountDummyMembers?.length ?? undefined}
              subscript={hiddenAccountMembers?.length ?? undefined}
              typographyStyle={{
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
              }}
            >
              {updatedMembers - hiddenAccountMembers?.length}
            </CustomNumber>
          </Info>
        </Box>
        <Box>
          <Info title="Projected">
            <Box ref={alertMailAnchor}>
              {headcountSummary &&
              (headcountSummary.projectedJoiningHeadcount > 0 ||
                headcountSummary.projectedLeavingHeadcount < 0) ? (
                <>
                  <Box
                    onMouseEnter={handleAlertMailMouseOver}
                    onMouseLeave={handleAlertMailClose}
                  >
                    <CustomNumber
                      typographyStyle={{
                        fontWeight: 600,
                        fontSize: '24px',
                        lineHeight: '100%',
                        color: '#666666',
                      }}
                    >
                      {headcountSummary.projectedHeadcount > 0 && '+'}
                      {headcountSummary.projectedHeadcount}
                    </CustomNumber>
                    <MyPortfolioHeaderPopover
                      anchorEl={alertMailAnchor}
                      handleClose={handleAlertMailClose}
                      handleMouseOver={handleAlertMailMouseOver}
                      isOpen={openMailPopover}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          alignContent: 'center',
                          color: 'text.secondary',
                          justifyContent: 'space-between',
                          gap: 1,
                          marginBottom: 1,
                        }}
                      >
                        <Typography>
                          {headcountSummary && projectedHeadcountMessage}
                        </Typography>
                      </Box>
                    </MyPortfolioHeaderPopover>
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="row" ref={buttonMailAnchor}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    ref={buttonMailAnchor}
                  >
                    <Button
                      variant="contained"
                      onClick={handleToggleModal}
                      size="small"
                      sx={{
                        backgroundColor: COLORS.HIGHLIGHTS.DANGER,
                        '&:hover': {
                          backgroundColor: COLORS.STATUS.CRITICAL,
                        },
                        height: '27px',
                      }}
                      onMouseOver={handleButtonMouseOver}
                      onMouseLeave={handleButtonMouseClose}
                    >
                      <CustomIcon
                        icon={IconName.Edit}
                        sx={{ color: '#fff', fontSize: '18px' }}
                      />
                      <Typography variant="body1" sx={{ marginLeft: 0.7 }}>
                        Add
                      </Typography>
                    </Button>
                  </Box>
                  <MyPortfolioHeaderPopover
                    anchorEl={buttonMailAnchor}
                    handleClose={handleButtonMouseClose}
                    handleMouseOver={handleAlertMailMouseOver}
                    isOpen={openAddButtonAlert}
                  >
                    <>
                      <Typography>
                        {`No projected headcounts available for this 
                        ${project ? 'project' : 'account'}.`}
                      </Typography>
                      <Typography>Please add a projected headcount.</Typography>
                    </>
                  </MyPortfolioHeaderPopover>
                </Box>
              )}
            </Box>
          </Info>
        </Box>
        <Box>
          {headcountSummary &&
            (headcountSummary.projectedJoiningHeadcount > 0 ||
              headcountSummary.projectedLeavingHeadcount < 0) && (
              <IconButton onClick={handleToggleModal} size="small">
                <CustomIcon icon={IconName.Edit} sx={{ color: '#666666' }} />
              </IconButton>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box>
          <GrossMarginSummary
            marginHighlights={budget.marginHighlights}
            grossMargin={budget?.grossMargin ?? undefined}
          />
        </Box>
        <Stack
          direction="row"
          alignItems="end"
          gap={1}
          sx={{ marginBottom: 1 }}
        >
          <IconButton onClick={calculateMargins}>
            <CustomIcon icon={IconName.Calculate} sx={{ color: '#666666' }} />
          </IconButton>
          <CsvDownloadButton style="icon" />
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
          gap: 2.5,
        }}
      >
        <Info title="Actual revenue">
          <CustomNumber
            typographyStyle={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '100%',
            }}
            info
          >
            {priceFormat({ value: account.revenue, hideEmptyCents: true })}
          </CustomNumber>
        </Info>

        <Tooltip
          sx={{
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
          title={revenueLastInvoiceDateFormated}
          placement="top"
        >
          <Box>
            <CustomIcon
              icon={IconName.Info}
              sx={{
                color: '#666666',
                alignSelf: 'flex-end',
                marginBottom: '2px',
                marginLeft: '8px',
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'min-content min-content',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Info title="Projected revenue">
          <CustomNumber
            typographyStyle={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '100%',
            }}
          >
            {priceFormat({
              value: account.projectedRevenue,
              hideEmptyCents: true,
            })}
          </CustomNumber>
        </Info>
        <Tooltip
          sx={{
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
          title={projectedRevenuePrompt}
          placement="top"
        >
          <Box>
            <CustomIcon
              icon={IconName.Info}
              sx={{
                color: '#666666',
                alignSelf: 'flex-end',
                marginBottom: '2px',
                marginLeft: '8px',
              }}
            />
          </Box>
        </Tooltip>
      </Box>

      <Info title="PDM">
        {account.portfolioDeliveryManager ? (
          <MemberInfoCell userInfo={account.portfolioDeliveryManager} />
        ) : (
          <NotAssignedMemberCell />
        )}
      </Info>
      <Info title="DM">
        {account.deliveryManager ? (
          <MemberInfoCell userInfo={account.deliveryManager} />
        ) : (
          <NotAssignedMemberCell />
        )}
      </Info>

      <EditHeadcountChangeModal
        handleCloseModal={handleToggleModal}
        isModalOpen={isModalOpen}
      />
    </Card>
  );
};

const NotAssignedMemberCell: React.FC = () => {
  return (
    <Stack direction="row" alignItems="center">
      <Avatar
        src={''}
        sx={{
          width: avatarMediumSize,
          height: avatarMediumSize,
          bgcolor: 'gray',
        }}
      />
      <Typography variant="h4" sx={{ marginLeft: 1, opacity: 0.7 }}>
        Not Assigned
      </Typography>
    </Stack>
  );
};
